import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import CityTable from '../components/CityTable';
import { useDeleteCity } from '../hooks/useCityMutations';
import type { City } from '@/shared/types';
import DynamicFilterBuilder from '@/shared/filtering/DynamicFilterBuilder';
import { ADMIN_FILTER_RESOURCES, type DynamicRelationLookups } from '@/shared/filtering/types';
import { useDynamicAdminList } from '@/shared/filtering/useDynamicAdminList';
import { countryApi } from '@/features/countries/api/country.api';
import { regionApi } from '@/features/regions/api/region.api';

export default function CityListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<City | null>(null);

  const list = useDynamicAdminList<City>(ADMIN_FILTER_RESOURCES.cities, { initialSortField: 'id', initialSortDir: 'asc' });
  const { data, isLoading } = list.searchQuery;
  const deleteMutation = useDeleteCity();

  const relationLookups = useMemo<DynamicRelationLookups>(() => ({
    'country.id': {
      fetchFn: ({ search, page, size }) => countryApi.list({ page, size, nameEn: search }),
      mapOption: (c: any) => ({ value: c.id, label: c.nameEn || c.nameAr || `#${c.id}` }),
      placeholder: t('cities.allCountries'),
    },
    'region.id': {
      fetchFn: ({ search, page, size }) => regionApi.list({ page, size, nameEn: search }),
      mapOption: (r: any) => ({ value: r.id, label: r.nameEn || r.nameAr || `#${r.id}` }),
      placeholder: t('cities.allRegions'),
    },
  }), [t]);

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSettled: () => setDeleteTarget(null) });
  };

  return (
    <div>
      <PageHeader title={t('cities.title')} actionLabel={t('cities.addNew')} onAction={() => navigate('/cities/create')} />
      <DynamicFilterBuilder resource={list.resource} metadata={list.metadataQuery.data} value={list.filters} onChange={list.setFilters} onReset={list.resetFilters} relationLookups={relationLookups} />
      <div style={{ marginTop: 16 }}>
        <CityTable
          data={data?.content || []}
          loading={isLoading}
          sortField={list.sortField}
          sortDir={list.sortDir}
          onSort={list.handleSort}
          onEdit={(id) => navigate(`/cities/${id}/edit`)}
          onDelete={(item) => setDeleteTarget(item)}
          page={data?.currentPage ?? 0}
          pageSize={list.size}
          total={data?.totalItems ?? 0}
          onPageChange={list.setPage}
        />
      </div>
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('cities.deleteTitle')}
        message={`${t('cities.deleteMessage')} "${deleteTarget?.nameEn}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
